/**
 * Hook to check if a store visit is already signed off
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

interface StoreSignoffStatus {
  storeId: number;
  isSignedOff: boolean;
  signedAt: string | null;
  signatureUrl: string | null;
  completionStatus: string;
  status: string;
}

export function useStoreSignoffStatus(storeId: number | null, operatorId?: number) {
  return useQuery<StoreSignoffStatus | null>({
    queryKey: ['store-signoff-status', storeId, operatorId],
    queryFn: async () => {
      if (!storeId) return null;

      const url = operatorId 
        ? `/api/mobile/store-signoff/status/${storeId}?operatorId=${operatorId}`
        : `/api/mobile/store-signoff/status/${storeId}`;
      
      const response = await apiFetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          // Store visit not found - not signed off
          return { 
            storeId, 
            isSignedOff: false, 
            signedAt: null, 
            signatureUrl: null,
            completionStatus: 'pending',
            status: 'pending'
          };
        }
        throw new Error(`Failed to fetch store signoff status: ${response.status}`);
      }

      return response.json();
    },
    enabled: !!storeId,
    staleTime: 30_000, // 30 seconds
    retry: 1,
  });
}

